import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';

export default function ApiDocsPage() {
  // OpenAPI spec for PRISAI
  const spec = {
    openapi: '3.0.3',
    info: {
      title: 'PRISAI API',
      description: 'Property Records Intelligence System with AI - API Documentation',
      version: '1.0.0',
    },
    servers: [
      {
        url: '/api',
        description: 'PRISAI API Server',
      },
    ],
    components: {
      securitySchemes: {
        bearerAuth: {
          type: 'http',
          scheme: 'bearer',
          bearerFormat: 'JWT',
        },
        apiKey: {
          type: 'apiKey',
          in: 'header',
          name: 'X-API-Key',
        },
      },
      schemas: {
        User: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            email: { type: 'string', format: 'email' },
            role: { type: 'string', enum: ['admin', 'user'] },
            created_at: { type: 'string', format: 'date-time' },
          },
        },
        Parcel: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            apn: { type: 'string' },
            address: { type: 'string' },
            city: { type: 'string' },
            county: { type: 'string' },
            state: { type: 'string' },
            zip: { type: 'string' },
            latitude: { type: 'number' },
            longitude: { type: 'number' },
            land_area: { type: 'number' },
            zoning: { type: 'string' },
            property_class: { type: 'string' },
            tax_status: { type: 'string' },
          },
        },
        Owner: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            name: { type: 'string' },
            entity_type: { type: 'string', enum: ['individual', 'company'] },
            mailing_address: { type: 'string' },
            mailing_city: { type: 'string' },
            mailing_state: { type: 'string' },
            mailing_zip: { type: 'string' },
            confidence: { type: 'number' },
          },
        },
        PropertyFeature: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            parcel_id: { type: 'string', format: 'uuid' },
            feature_type: { type: 'string' },
            feature_key: { type: 'string' },
            value: { type: 'string' },
            unit: { type: 'string' },
            confidence: { type: 'number' },
            source: { type: 'string', enum: ['ai', 'record'] },
          },
        },
        Listing: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            parcel_id: { type: 'string', format: 'uuid' },
            status: { type: 'string', enum: ['for_sale', 'pending', 'sold', 'off_market'] },
            list_price: { type: 'integer' },
            sold_price: { type: 'integer' },
            list_date: { type: 'string', format: 'date' },
            sold_date: { type: 'string', format: 'date' },
            days_on_market: { type: 'integer' },
          },
        },
        IngestionJob: {
          type: 'object',
          properties: {
            id: { type: 'string', format: 'uuid' },
            status: { type: 'string', enum: ['queued', 'running', 'completed', 'failed'] },
            job_type: { type: 'string', enum: ['ingest', 'enrich', 'export'] },
            records_processed: { type: 'integer' },
            records_created: { type: 'integer' },
            records_updated: { type: 'integer' },
            errors: { type: 'string' },
          },
        },
      },
    },
    paths: {
      '/auth/login': {
        post: {
          tags: ['Authentication'],
          summary: 'Login with email/password',
          requestBody: {
            required: true,
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  required: ['email', 'password'],
                  properties: {
                    email: { type: 'string' },
                    password: { type: 'string' },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description: 'Login successful',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      user: { $ref: '#/components/schemas/User' },
                      token: { type: 'string' },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '/auth/logout': {
        post: {
          tags: ['Authentication'],
          summary: 'Logout current session',
          security: [{ bearerAuth: [] }],
          responses: { '200': { description: 'Logout successful' } },
        },
      },
      '/auth/me': {
        get: {
          tags: ['Authentication'],
          summary: 'Get current user',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          responses: {
            '200': {
              description: 'Current user',
              content: {
                'application/json': {
                  schema: { $ref: '#/components/schemas/User' },
                },
              },
            },
          },
        },
      },
      '/parcels': {
        get: {
          tags: ['Parcels'],
          summary: 'List/search parcels',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'q', in: 'query', schema: { type: 'string' } },
            { name: 'state', in: 'query', schema: { type: 'string' } },
            { name: 'county', in: 'query', schema: { type: 'string' } },
            { name: 'zip', in: 'query', schema: { type: 'string' } },
            { name: 'page', in: 'query', schema: { type: 'integer' } },
            { name: 'limit', in: 'query', schema: { type: 'integer' } },
          ],
          responses: {
            '200': {
              description: 'List of parcels',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      data: { type: 'array', items: { $ref: '#/components/schemas/Parcel' } },
                      meta: { type: 'object' },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '/parcels/{id}': {
        get: {
          tags: ['Parcels'],
          summary: 'Get parcel details',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
          ],
          responses: {
            '200': {
              description: 'Parcel details',
              content: {
                'application/json': {
                  schema: { $ref: '#/components/schemas/Parcel' },
                },
              },
            },
          },
        },
      },
      '/parcels/{id}/features': {
        get: {
          tags: ['Parcels'],
          summary: 'Get parcel features',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
          ],
          responses: {
            '200': {
              description: 'List of features',
              content: {
                'application/json': {
                  schema: {
                    type: 'array',
                    items: { $ref: '#/components/schemas/PropertyFeature' },
                  },
                },
              },
            },
          },
        },
      },
      '/parcels/{id}/features/enrich': {
        post: {
          tags: ['AI Enrichment'],
          summary: 'Trigger AI enrichment for a parcel',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
          ],
          requestBody: {
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    image_urls: { type: 'array', items: { type: 'string' } },
                    parcel_metadata: { type: 'object' },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description: 'Enrichment job queued',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: { job_id: { type: 'string' } },
                  },
                },
              },
            },
          },
        },
      },
      '/owners': {
        get: {
          tags: ['Owners'],
          summary: 'List/search owners',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          responses: {
            '200': {
              description: 'List of owners',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      data: { type: 'array', items: { $ref: '#/components/schemas/Owner' } },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '/owners/{id}': {
        get: {
          tags: ['Owners'],
          summary: 'Get owner details',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
          ],
          responses: {
            '200': {
              description: 'Owner details',
              content: {
                'application/json': {
                  schema: { $ref: '#/components/schemas/Owner' },
                },
              },
            },
          },
        },
      },
      '/owners/{id}/parcels': {
        get: {
          tags: ['Owners'],
          summary: "Get owner's parcels",
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          parameters: [
            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
          ],
          responses: {
            '200': {
              description: 'List of parcels',
              content: {
                'application/json': {
                  schema: {
                    type: 'array',
                    items: { $ref: '#/components/schemas/Parcel' },
                  },
                },
              },
            },
          },
        },
      },
      '/analytics/summary': {
        get: {
          tags: ['Analytics'],
          summary: 'Get analytics summary',
          security: [{ bearerAuth: [] }, { apiKey: [] }],
          responses: {
            '200': {
              description: 'Analytics summary',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: {
                      total_parcels: { type: 'integer' },
                      total_owners: { type: 'integer' },
                      ai_enriched_parcels: { type: 'integer' },
                      enrichment_rate: { type: 'number' },
                      avg_confidence: { type: 'number' },
                      data_completeness: { type: 'number' },
                      ingestion_success_rate: { type: 'number' },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '/admin/ingest': {
        post: {
          tags: ['Admin'],
          summary: 'Trigger ingestion job',
          security: [{ bearerAuth: [] }],
          requestBody: {
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    source_id: { type: 'string', format: 'uuid' },
                  },
                },
              },
            },
          },
          responses: {
            '200': {
              description: 'Job queued',
              content: {
                'application/json': {
                  schema: {
                    type: 'object',
                    properties: { job_id: { type: 'string' } },
                  },
                },
              },
            },
          },
        },
      },
      '/admin/jobs': {
        get: {
          tags: ['Admin'],
          summary: 'List ingestion jobs',
          security: [{ bearerAuth: [] }],
          responses: {
            '200': {
              description: 'List of jobs',
              content: {
                'application/json': {
                  schema: {
                    type: 'array',
                    items: { $ref: '#/components/schemas/IngestionJob' },
                  },
                },
              },
            },
          },
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">API Documentation</h1>
        <p className="text-gray-600">Interactive API documentation powered by Swagger</p>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4" style={{ minHeight: '80vh' }}>
        <SwaggerUI spec={spec} />
      </div>
    </div>
  );
}
